'use client';

import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { RecipeHero } from '@/components/recipe/recipe-hero';
import { Skeleton, SkeletonGroup } from '@/components/ui/skeleton';
import { readCachedRecipeCard, type CachedRecipeCard } from '@/lib/utils/recipe-card-cache';
import styles from './loading.module.css';


/**
 * Reads the recipe card data the list page wrote to sessionStorage on click,
 * and renders a real <RecipeHero> immediately — title + image + meta — while
 * the server-side queries for the rest of the recipe are still in flight.
 *
 * Falls back to a hero-shaped skeleton for cold loads (deep-link, refresh,
 * new tab, or expired cache).
 *
 * Always renders exactly one hero — server-side renders the skeleton, and the
 * client swaps to the real hero in the first effect tick (~10ms post-hydration).
 */
export function OptimisticHero() {
  const params = useParams<{ slug: string }>();
  const [card, setCard] = useState<CachedRecipeCard | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Intentional cascade: SSR renders the skeleton (hydrated=false), then
    // post-hydration we flip + read sessionStorage. useSyncExternalStore would
    // be cleaner but the resolved card needs SSR to render *nothing*
    // (skeleton) regardless of client cache state — null initial is correct.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHydrated(true);
    if (!params?.slug) return;
    setCard(readCachedRecipeCard(params.slug));
  }, [params?.slug]);

  if (!hydrated || !card || !params?.slug) {
    return <HeroSkeleton />;
  }

  return (
    <RecipeHero
      title={card.title}
      description={card.description}
      imageUrl={card.imageUrl}
      slug={params.slug}
      prepTimeMinutes={card.prepTimeMinutes}
      cookTimeMinutes={card.cookTimeMinutes}
      servings={card.servings}
      caloriesPerServing={card.caloriesPerServing}
      categoryName={card.categoryName}
    />
  );
}

function HeroSkeleton() {
  return (
    <SkeletonGroup label="Loading recipe…">
      <div className={styles.hero}>
        <Skeleton height="100%" className={styles.heroImage} />
        <div className={styles.heroContent}>
          <Skeleton height="16px" width="100px" />
          <Skeleton height="48px" />
          <Skeleton height="80px" />
          <div className={styles.metaRow}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height="60px" width="80px" />
            ))}
          </div>
        </div>
      </div>
    </SkeletonGroup>
  );
}
